import React, { useState, Suspense } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { FormattedMessage } from 'react-intl'
import { generateTimeOptions, generateQuickSelectorOptions } from './helpers'
import {
  LayoutContainer,
  ModalHeader,
  TimePickerContainer,
  DateSelectContainer,
  DateSelectPanel,
  SettleButton,
  InstantPickupView,
} from './styled'
import {
  getSoonestSchedule, getStoreSchedule, getValidTime, generateValidPickupTimes,
} from '../../utils/time'
import Loading from '../Loading'

const Picker = React.lazy(() => import('../WheelPicker'))

function TimePicker({
  store, onChange, pickupTime, loading, order,
}) {
  const storeSchedule = getStoreSchedule(store.opening_hours)
  const dayKey = Object.keys(storeSchedule)[0]
  const previousOpeningTime = storeSchedule[dayKey].opening
  const nextClosingTime = storeSchedule[dayKey].closing
  const in15Mins = moment().add(15, 'minutes')
  const showQuickSelectors = in15Mins.isAfter(previousOpeningTime) && in15Mins.isBefore(nextClosingTime)
  const asapSchedule = getSoonestSchedule(store.opening_hours, null, store.min_order_time)
  const timeSelection = generateValidPickupTimes(storeSchedule, dayKey, store.min_order_time)

  // the soonest available time
  const initialTime = {
    hour: pickupTime ? pickupTime.format('HH') : asapSchedule.pickup.format('HH'),
    minute: pickupTime ? pickupTime.format('mm') : asapSchedule.pickup.format('mm'),
    isInstantOrder: false,
  }
  const [values, setValues] = useState(initialTime)

  // quick button pickup time selectors
  const quickSelectors = generateQuickSelectorOptions(store, order)

  // available times
  const options = generateTimeOptions(store)

  function onClickQuickSelector(option) {
    return () => {
      if (option.value) {
        const time = pickupTime
          ? pickupTime.clone().add(option.value, 'minute')
          : moment().add(option.value, 'minute')

        let validTime = getValidTime(timeSelection,
          { hour: time.format('HH'), minute: time.format('mm') })

        if (pickupTime) validTime = time

        if (validTime) {
          setValues({
            hour: time.format('HH'),
            minute: time.format('mm'),
            isInstantOrder: false,
          })
        } else {
          setValues({ ...initialTime })
        }
      } else {
        // asap(sofort) selected
        setValues({
          hour: asapSchedule.pickup.format('HH'),
          minute: asapSchedule.pickup.format('mm'),
          isInstantOrder: true,
        })
      }
    }
  }

  function onChangeTime(name, value) {
    const selectedTime = name === 'hour'
      ? { hour: value, minute: values.minute }
      : { hour: values.hour, minute: value }
    let validTime = getValidTime(timeSelection, selectedTime)

    if (pickupTime) {
      const time = moment().set('hour', selectedTime.hour).set('minute', selectedTime.minute)
      if (time.isAfter(pickupTime)) {
        validTime = { hour: time.format('HH'), minute: time.format('mm') }
      }
    }

    if (validTime) {
      setValues({
        hour: validTime.hour,
        minute: validTime.minute,
        isInstantOrder: false,
      })
    } else {
      // rollback to the soonest available time if any invalid(or past) time selected
      setValues({ ...initialTime })
    }
  }

  function setPickupTime() {
    const { hour, minute, isInstantOrder } = values
    const newPickupTime = moment().set('hour', hour).set('minute', minute)
    onChange({ time: newPickupTime, isInstantOrder })
  }

  return (
    <LayoutContainer>
      <ModalHeader>
        <FormattedMessage id="components.TimePicker.Title" />
      </ModalHeader>
      {showQuickSelectors && (
        <DateSelectContainer>
          {quickSelectors.map(quickOption => (
            <DateSelectPanel
              key={quickOption.label}
              onClick={onClickQuickSelector(quickOption)}
            >
              {quickOption.label}
            </DateSelectPanel>
          ))}
        </DateSelectContainer>
      )}
      <TimePickerContainer>
        {values.isInstantOrder ? (
          <InstantPickupView>
            <FormattedMessage
              id="components.TimePicker.InstantOrderDescription"
              values={{ min_prep_time: Math.ceil(store.min_order_time / 60) }}
            />
          </InstantPickupView>
        ) : (
          <Suspense fallback={<Loading />}>
            <Picker
              optionGroups={options}
              valueGroups={values}
              onChange={onChangeTime}
              itemHeight={50}
            />
          </Suspense>
        )}
      </TimePickerContainer>
      <SettleButton onClick={setPickupTime} loading={loading}>
        <FormattedMessage
          id="components.TimePicker.ResolveButton"
          values={{ time: `${values.hour}:${values.minute}` }}
        />
      </SettleButton>
    </LayoutContainer>
  )
}

TimePicker.propTypes = {
  store: PropTypes.object.isRequired,
  pickupTime: PropTypes.object, // Moment object
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  order: PropTypes.object,
}

TimePicker.defaultProps = {
  pickupTime: null,
  loading: false,
  order: null,
}

export default TimePicker
